import React, { useEffect } from 'react';

import { useParams, useLocation } from 'react-router-dom';

import { Notifier } from '../../system/services/notificationManager';
import { useAuthenticator } from '../../auth/AuthenticationContext';
import { updateCacheFromSubscriptionEvent } from '../../../helpers/subscriptionUtils';
// import {
//   useInjectableComponents,
//   // BottomMenuItems,
// } from '../../system/services/injectableComponentsManager';
// import { basePropertyMenuItems } from './ui/base-property-context-menu';

import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_Row,
  useMaterialReactTable,
} from 'material-react-table';

import { Box } from '@mui/material';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';
// import AddIcon from '@mui/icons-material/Add';

import {
  AlertTrigger,
  AlertTriggerTriggerCriteriaSelectionCriteriaCriterion,
  EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType,
  Maybe,
  useAlertTriggerListByPropertyIdQuery,
  useAlertTriggerListByPropertyIdUpdateSubscription,
  // Property,
  // usePropertyQuery,
} from '../../../types/generated-types';

import {
  AlertTriggerEditMode,
  AlertTriggerComparatorToStringMap,
  AlertTriggerSelectionBasis,
} from '../../../common/types';
import {
  EditableAlertTriggerSelectionCriteriaValue,
  RowData,
} from './types/propertyAlertTriggerRowData';
import { PropertyAlertTriggerCardView } from './property-alert-trigger-card-view';
import { AuthorizedUser } from '../../auth/models';

import {
  Celsius,
  CubicFeetPerMinute,
  Fahrenheit,
  LitersPerSecond,
} from '../../system/models/temperatureUnits';
import {
  convertToAirFlowUnits,
  formatAirflowString,
  convertToHumidityUnits,
  formatHumidityString,
  convertToPressureUnits,
  formatPressureString,
  convertToTempUnits,
  formatTemperatureString,
} from '../devices/util';

type DateFormatOptions = {
  day: '2-digit';
  month: '2-digit';
  year: 'numeric';
};
function formatDate(dateAsString: string): string {
  const date = new Date(dateAsString);
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  } as DateFormatOptions;
  const formattedDate = new Intl.DateTimeFormat('en-GB', options).format(date);
  return formattedDate;
}

// TODO: Peter: we should be converting between PSI and kPa using the conversion routine:
//  1 PSI = 6.89476 kPa.
//

/**
 * Returns an object as follows:
 *  {
 *    formattedCriterion: A display string 'description' of the criterion suitable for general user viewing.
 *    convertedCriterion: An augmented 'criterion' object enhanced with these additional fields:
 *     displayValue: The current values converted to the user's preferred units,
 *     editableValue: editableValue,
 *     valueDisplayUnits: valueDisplayUnits,
 *     valueConverter: valueConverter,
 *  }
 * @param c
 * @param user
 * @param baseName
 */
export function processAlertTriggerSelectionCriterion(
  c: Maybe<AlertTriggerTriggerCriteriaSelectionCriteriaCriterion>,
  user: AuthorizedUser | undefined,
  baseName: string = AlertTriggerSelectionBasis.DEVICE,
) {
  if (c === null || c === undefined) {
    return undefined;
  }
  const preferredTempUnits = user?.preferredUnits ?? Fahrenheit;
  const tempPrecision = preferredTempUnits === Fahrenheit ? 0 : 1;
  const attributeParts = (c.selectionPath ?? '').split('.');
  const attributeName =
    (attributeParts[attributeParts.length - 1] === 'value'
      ? attributeParts[0]
      : c.selectionPath) ?? 'Some attribute';
  const operator =
    AlertTriggerComparatorToStringMap[c.comparator] ?? 'Unknown comparator';
  const operatorDisplay = Array.isArray(operator)
    ? operator.join(' ')
    : operator;
  let editableValues: EditableAlertTriggerSelectionCriteriaValue[] =
    c.values.map((val) => val ?? '');
  let displayValues = [...c.values];
  let valueDisplayUnits = '';

  let valueConverter = (value: EditableAlertTriggerSelectionCriteriaValue) =>
    `${value}`;

  switch (c.valueType) {
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.Boolean:
      editableValues = c.values.map((val) => val === 'true');
      break;
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.Number:
      editableValues = c.values.map((val) => parseFloat(val ?? '0'));
      break;
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.String:
      break;
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.Date:
      editableValues = c.values.map((val) => new Date(val ?? 0));
      displayValues = c.values.map((val) => formatDate(val ?? '0'));
      break;
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.Airflow:
      editableValues = c.values.map(
        (val) =>
          convertToAirFlowUnits(
            parseFloat(val ?? '0') ?? 0,
            user?.preferredAirFlowUnits ?? CubicFeetPerMinute,
            LitersPerSecond,
            0,
          ) ?? 0,
      );
      displayValues = c.values.map((_val, index) =>
        formatAirflowString(
          editableValues[index] as number,
          user?.preferredAirFlowUnits ?? CubicFeetPerMinute,
        ),
      );
      valueDisplayUnits = `${
        user?.preferredAirFlowUnits ?? CubicFeetPerMinute
      }`;
      valueConverter = (value: EditableAlertTriggerSelectionCriteriaValue) => {
        const airflowValue = parseFloat(`${value}`);
        return `${
          convertToAirFlowUnits(
            airflowValue,
            LitersPerSecond,
            user?.preferredAirFlowUnits ?? CubicFeetPerMinute,
            0,
          ) ?? 0
        }`;
      };
      break;
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.Humidity:
      editableValues = c.values.map(
        (val) => convertToHumidityUnits(parseFloat(val ?? '0')) ?? 0,
      );
      displayValues = c.values.map((_val, index) =>
        formatHumidityString((editableValues[index] as number) ?? 0),
      );
      valueDisplayUnits = '%';
      break;
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.Pressure:
      editableValues = c.values.map(
        (val) => convertToPressureUnits(parseFloat(val ?? '0')) ?? 0,
      );
      displayValues = c.values.map((_val, index) =>
        formatPressureString(editableValues[index] as number),
      );
      valueDisplayUnits = 'PSI';
      break;
    case EnumAlertTriggerTriggerCriteriaSelectionCriteriaCriterionValueType.Temperature:
      editableValues = c.values.map(
        (val) =>
          convertToTempUnits(
            parseFloat(val ?? '0') ?? 0,
            preferredTempUnits,
            Celsius,
            tempPrecision,
          ) ?? 0,
      );
      displayValues = c.values.map((_val, index) =>
        formatTemperatureString(
          editableValues[index] as number,
          user?.preferredUnits ?? Fahrenheit,
        ),
      );
      valueDisplayUnits = `\xB0${user?.preferredUnits ?? Fahrenheit}`;
      valueConverter = (value: EditableAlertTriggerSelectionCriteriaValue) => {
        const tempValue = parseFloat(`${value}`);
        return `${convertToTempUnits(
          tempValue,
          Celsius,
          preferredTempUnits,
          1,
        )}`;
      };
      break;
    default:
      console.log('hit the default branch');
      break;
  }

  const formattedCriterion = `${baseName}'s ${attributeName} ${operatorDisplay} ${displayValues.length === 1 ? displayValues[0] : '[' + displayValues.join(', ') + ']'}`;
  const convertedCriterion = {
    ...c,
    displayValues: displayValues,
    editableValues: editableValues,
    valueDisplayUnits: valueDisplayUnits,
    valueConverter: valueConverter,
  } as RowData['rawCriteria'][number];
  return {
    formattedCriterion,
    convertedCriterion,
    attributeName,
    operator: operatorDisplay,
  };
}

function formatData(
  data: Partial<AlertTrigger>[],
  user: AuthorizedUser | undefined,
): RowData[] {
  const formattedData: RowData[] = [];
  [...data]
    .sort((a, b) => {
      const nameA = a.name ?? '';
      const nameB = b.name ?? '';
      const enabledA = !!a.enabled;
      const enabledB = !!b.enabled;

      // Default order: enabled triggers first.
      if (enabledA !== enabledB) {
        return enabledA ? -1 : 1;
      }

      if (a.editMode !== b.editMode) {
        return a.editMode === AlertTriggerEditMode.LOCKED ? 1 : -1;
      } else {
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
      }
    })
    .forEach((row) => {
      if (row?._id) {
        const criteriaList = row.triggerCriteria?.selectionCriteria?.criteria;
        const criteria = row.triggerCriteria?.selectionCriteria?.criterion
          ? [row.triggerCriteria?.selectionCriteria?.criterion]
          : criteriaList || [];

        let entityTypes = row.entityTypes ? row.entityTypes : [];
        entityTypes = entityTypes.includes('Device')
          ? ['Any Device']
          : entityTypes.includes('Source')
            ? ['Any Source']
            : (entityTypes as Array<string>);

        const formattedCriteria: string[] = [];
        const convertedCriteria: RowData['rawCriteria'] = [];

        for (const c of criteria) {
          const processResult = processAlertTriggerSelectionCriterion(
            c,
            user,
            row.triggerCriteria?.basisType,
          );

          if (processResult) {
            const { convertedCriterion, formattedCriterion } = processResult;
            formattedCriteria.push(formattedCriterion);
            if (convertedCriterion) {
              convertedCriteria.push(convertedCriterion);
            }
          }
        }

        formattedData.push({
          id: row._id,
          name: row.name ?? 'Unnamed alert trigger',
          entityTypes: entityTypes,
          basisType: row.triggerCriteria?.basisType,
          basisName: row.triggerCriteria?.basisName,
          levels: row.triggerCriteria?.levels ?? 1,
          triggerCriteria: formattedCriteria,
          rawCriteria: convertedCriteria,
          enabled: !!row.enabled,
        });
      }
    });
  return formattedData;
}

export function PropertyAlertTriggersList({
  occupiedHeight = 0,
  innerRef = null,
  onRowClick = () => {},
}: {
  occupiedHeight?: number;
  innerRef?: React.RefObject<HTMLDivElement> | null;
  onRowClick?: (row: MRT_Row<RowData>) => void;
}) {
  const [alertTriggers, setAlertTriggers] = React.useState<RowData[]>([]);
  // TODO: Peter: Add this back in when we enhance trigger management (adding new ones, for example).
  //  const { setContextMenuItems } = useInjectableComponents();
  //  const { pathname } = useLocation();
  //  const { data: propertyData } = usePropertyQuery({ variables: { _id: id } });

  const { id } = useParams<{ id: string }>();

  const { data, error } = useAlertTriggerListByPropertyIdQuery({
    variables: { propertyId: id ?? '' },
    fetchPolicy: 'network-only',
  });

  const { user } = useAuthenticator();

  const [triggerColumns, setTriggerColumns] = React.useState<
    MRT_ColumnDef<RowData>[]
  >([]);

  const isSmall = useIsSmallScreen();

  const { pathname } = useLocation();
  const isEditPropertyMode = pathname.includes('edit-property');

  useAlertTriggerListByPropertyIdUpdateSubscription({
    variables: { propertyId: id ?? '' },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (error) {
      Notifier.error('Error loading alert triggers: ' + error.message);
    } else if (data?.alertTriggersByPropertyId) {
      setAlertTriggers(formatData(data.alertTriggersByPropertyId, user));
    }
  }, [data, error, user]);

  useEffect(() => {
    const nameColumn: MRT_ColumnDef<RowData> = isSmall
      ? ({
          accessorKey: 'name',
          header: 'Alert Trigger Name',
          grow: true,
          Cell: PropertyAlertTriggerCardView,
        } as MRT_ColumnDef<RowData>)
      : {
          accessorKey: 'name',
          header: 'Alert Trigger Name',
          grow: true,
        };

    const columns: MRT_ColumnDef<RowData>[] = [
      nameColumn,
      ...((isSmall
        ? []
        : [
            {
              id: 'entityTypes',
              accessorFn: (row) => (row.entityTypes ?? []).join(', '),
              header: 'Type',
              size: 200,
              Cell: ({ row }) => {
                const types = row.original.entityTypes ?? [];
                return types.includes('Device') ? (
                  'Any Device'
                ) : types.includes('Source') ? (
                  'Any Source'
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                    }}
                  >
                    {types.map((type) => (
                      <div key={type}>{type}&nbsp;</div>
                    ))}
                  </div>
                );
              },
            },
            {
              accessorKey: 'triggerCriteria',
              header: 'Trigger Criteria',
              maxSize: 350,
              Cell: ({ row }) => {
                const triggerCriteria = row.original.triggerCriteria ?? [];
                return (
                  <div>
                    {triggerCriteria.map((type, index) => (
                      <div key={`${row.original.id}-${index}`}>{type}</div>
                    ))}
                  </div>
                );
              },
            },
          ]) as MRT_ColumnDef<RowData>[]),
    ];

    setTriggerColumns(columns);
  }, [isSmall]);

  // TODO: Peter: Alerts: Add this back in when we enhance the trigger editing feature.
  // useEffect(() => {
  //   if (propertyData?.property) {
  //     const contextMenuItems: BottomMenuItems = [
  //       ...basePropertyMenuItems(id, pathname, [
  //         {
  //           id: 'add-alert-trigger-menu-item',
  //           label: 'Add Alert Trigger',
  //           icon: <AddIcon />,
  //           navTarget: (path: string) => {
  //             return `/properties/${id}/alert-triggers/add-alert-trigger`;
  //             // const pathComponent = path.split('/').slice(0, 4);
  //             // return pathComponent.slice(0, 4).join('/') + '/add-alert-trigger';
  //           },
  //           permit: 'createAlertTrigger',
  //         },
  //       ]),
  //     ];
  //
  //     setContextMenuItems(contextMenuItems, propertyData.property as Property);
  //   }
  //   return () => {
  //     setContextMenuItems(undefined, undefined);
  //   };
  // }, [propertyData, id, pathname]);

  const table = useMaterialReactTable({
    layoutMode: 'grid',
    defaultColumn: {
      enableColumnActions: false,
      sortingFn: 'alphanumeric',
      muiTableHeadCellProps: {
        align: 'left',
      },
      muiTableBodyCellProps: {
        align: 'left',
      },
    },
    columns: triggerColumns,
    data: (alertTriggers ?? []) as RowData[],
    // enableColumnFilterModes: true,
    // enableColumnOrdering: true,
    // enableGrouping: true,
    // enableColumnPinning: true,
    // enableFacetedValues: true,
    // enableRowActions: true,
    // enableRowSelection: true,
    enableStickyHeader: true,
    enableDensityToggle: true,
    initialState: {
      density: 'compact',
      showColumnFilters: false,
      showGlobalFilter: false,
      columnOrder: ['name', 'entityTypes', 'triggerCriteria'],
    },
    renderTopToolbar: false,
    renderBottomToolbar: false,
    enablePagination: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiTableBodyProps: {
      sx: {
        color: 'secondary.main',
        fontWeight: 800,
        fontSize: '18px',
        '& tr:nth-of-type(even)': {
          backgroundColor: 'rgba(241, 241, 241, 0.3) !important',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const rowCursor = isEditPropertyMode ? 'pointer' : 'default';
      return {
        ...(isEditPropertyMode
          ? {
              onClick: (event: React.MouseEvent) => {
                event.preventDefault();
                event.stopPropagation();
                onRowClick(row);
              },
            }
          : {}),
        sx: {
          height: 'auto',
          cursor: rowCursor,
          fontStyle: !row.original.enabled ? 'italic !important' : 'inherit',
          '& td': {
            color: !row.original.enabled ? 'primary.main' : 'secondary.main',
            cursor: rowCursor,
            fontWeight: !row.original.enabled ? 'normal' : '600',
          },
          '& div': {
            cursor: rowCursor,
          },
        },
      };
    },
    muiTableContainerProps: {
      style: {
        maxHeight: `calc(100vh - ${occupiedHeight}px)`,
      },
    },
  });

  return (
    <Box
      ref={innerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        alignItems: 'stretch',
        cursor: isEditPropertyMode ? 'pointer' : 'default',
      }}
    >
      <MaterialReactTable table={table} />
    </Box>
  );
}
